// hooks/iframe/useChatState.js
import { useState, useEffect, useCallback, useRef } from 'react';
import { usePersistedChat } from './usePersistedChat';
import { useMessageSync } from './useMessageSync';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export const useChatState = (agentId, apiKey) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [chatId, setChatId] = useState(null);
  const [chatState, setChatState] = useState('active');
  const [showForm, setShowForm] = useState(false);
  const [collectedFormData, setCollectedFormData] = useState(null);
  
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ✨ NOVO: Controle para evitar múltiplas atualizações
  const lastSyncTimestampRef = useRef(0);
  const isUpdatingMessagesRef = useRef(false);

  const {
    saveChatId,
    getPersistedChatId,
    fetchChatData,
    fetchChatMessages,
    processMessagesForDisplay,
    clearPersistedChat
  } = usePersistedChat(agentId, apiKey);

  // ✨ FUNÇÃO OTIMIZADA: Atualiza mensagens de forma inteligente
  const handleNewHandoffMessages = useCallback((apiMessages) => {
    // Evitar atualizações simultâneas
    if (isUpdatingMessagesRef.current) {
      console.log('[Message Sync] Atualização em andamento, ignorando...');
      return;
    }

    isUpdatingMessagesRef.current = true;

    try {
      console.log('[Message Sync] Processando', apiMessages.length, 'mensagens da API');
      
      const processedMessages = processMessagesForDisplay(apiMessages);

      console.log('[Message Sync]', processedMessages.length, 'mensagens processadas');

      // Atualizar timestamp da última sincronização
      lastSyncTimestampRef.current = Date.now();

      // ✨ Substituir todas as mensagens (fonte da verdade é o backend)
      setMessages(processedMessages);
      
    } catch (error) {
      console.error('[Message Sync] Erro ao processar mensagens:', error);
    } finally {
      isUpdatingMessagesRef.current = false;
    }
  }, [processMessagesForDisplay]);

  const { syncNow } = useMessageSync(
    chatId, 
    chatState, 
    apiKey, 
    isOpen,
    handleNewHandoffMessages,
    setChatState
  );

  useEffect(() => {
    const initializeAgentAndChat = async () => {
      if (!agentId) return;
      
      try {
        setLoading(true);
        setError(null);

        const persistedChatId = getPersistedChatId();
        
        let headers = {
          'Content-Type': 'application/json',
        }

        if (apiKey.startsWith("pawsf")) {
          headers['X-API-KEY'] = apiKey
        } else {
          headers['Authorization'] = `Bearer ${apiKey}`
        }

        const promises = [
          fetch(`${API_BASE_URL}/agents/${agentId}`, {
            headers: headers
          }).then(res => res.json())
        ];
        
        // Se tem chat persistido, adiciona promises
        if (persistedChatId) {
          promises.push(fetchChatData(persistedChatId));
          promises.push(fetchChatMessages(persistedChatId));
        }
        
        // Executa tudo em paralelo
        const results = await Promise.allSettled(promises);
        
        // Processa resultado do agente
        let agentData = null;
        if (results[0].status === 'fulfilled') {
          agentData = results[0].value.data || results[0].value;
          setAgent(agentData);
        } else {
          throw new Error('Falha ao carregar agente');
        }

        // Processa chat persistido se existir
        let chatLoaded = false;
        
        if (persistedChatId && results[1]?.status === 'fulfilled' && results[1].value) {
          const chatData = results[1].value;
          const state = chatData.state || 'active';
          setChatState(state);
          
          // Só carrega o chat se estiver em estado válido (incluindo ended para ver mensagem final)
          if (state === 'active' || state === 'paused' || state === 'handoff' || state === 'ended') {
            setChatId(persistedChatId);
            
            // Carrega mensagens se disponível
            if (results[2]?.status === 'fulfilled' && results[2].value) {
              const processedMessages = processMessagesForDisplay(results[2].value);
              
              if (processedMessages.length > 0) {
                setMessages(processedMessages);
                chatLoaded = true;
              }
            }
          }
        }

        // Se não carregou chat válido, cria novo
        if (!chatLoaded) {
          generateNewChatId();
          
          // Inicializa com welcome message se houver
          if (agentData?.widget_settings?.welcome_message) {
            setMessages([{
              id: 1,
              type: 'assistant',
              text: agentData.widget_settings.welcome_message,
              timestamp: new Date()
            }]);
          }
        }

        // Verifica se deve mostrar formulário
        if (agentData?.widget_settings?.form?.active && !chatLoaded) {
          setShowForm(true);
        }

      } catch (err) {
        console.error("Error initializing:", err);
        setError(err.message || 'Erro ao carregar dados');
      } finally {
        setLoading(false);
      }
    };
    
    initializeAgentAndChat();
  }, [agentId, apiKey]);

  const handleEndChat = async () => {
    if (!chatId) return;
    
    try {
      setChatState('ended');
      clearPersistedChat()

      let headers = {
          'Content-Type': 'application/json',
      }

      if (apiKey.startsWith("pawsf")) {
        headers['X-API-KEY'] = apiKey
      } else {
        headers['Authorization'] = `Bearer ${apiKey}`
      }

      const response = await fetch(`${API_BASE_URL}/chats/${chatId}/state`, {
        method: 'PUT',
        headers: headers,
        body: JSON.stringify({
          state: 'ended',
          reason: 'Usuário encerrou a conversa'
        })
      });

      if (response.ok) {
        console.log('Chat encerrado:', chatId);
      } else {
        console.error('Erro ao encerrar chat:', response.status);
      }
    } catch (error) {
      console.error('Erro ao encerrar chat:', error);
    }
  };

  const generateNewChatId = () => {
    const newChatId = `chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    setChatId(newChatId);
    saveChatId(newChatId);
    console.log('Novo Chat ID gerado e salvo:', newChatId);
  };

  useEffect(() => {
    if (chatId && !showForm) {
      saveChatId(chatId);
    }
  }, [chatId, showForm, saveChatId]);

  // Handlers
  const handleOpenChat = () => {
    setIsOpen(true);
  };

  const handleCloseChat = () => {
    setIsOpen(false);
  };

  const handleFormSubmit = (formData) => {
    setCollectedFormData(formData);
    setShowForm(false);
    console.log('Dados do formulário coletados:', formData);
  };

  const handleNewChat = () => {
    clearPersistedChat();
    setMessages([]);
    setChatId(null);
    setChatState('active');
    generateNewChatId();
    
    if (agent?.widget_settings?.welcome_message) {
      setMessages([{
        id: 1,
        type: 'assistant',
        text: agent.widget_settings.welcome_message,
        timestamp: new Date()
      }]);
    }
  };

  return {
    // Estados do agente
    agent, 
    loading, 
    error,
    // Estados do chat
    isOpen,
    messages,
    setMessages,
    chatId,
    chatState,
    setChatState,
    showForm,
    collectedFormData,
    // Handlers
    handleOpenChat,
    handleCloseChat,
    handleFormSubmit,
    handleNewChat,
    handleEndChat
  };
};